<template>
    <div>
        <DCard
            padding
        >
            <DRow
                class="mb-3"
                align-vertical="end"
            >
                <DCol sm="2">
                    <DSelect
                        v-model="filters.status"
                        :label="__('tools.service_fees.status.status')"
                        :options="statusOptionsMapped"
                        :is-valid="isFormFieldValid('status')"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        :options="marketRegionOptions"
                        :label="__('tools.service_fees.table.market_region')"
                        :search="true"
                        :selection="true"
                        :options-empty-placeholder="__('tools.service_fees.market_region')"
                        selection-type="text"
                        @update="updateMarketRegions"
                    />
                </DCol>
                <DCol sm="2">
                    <DInput
                        v-model="filters.valid_from"
                        :label="__('tools.service_fees.table.valid_from')"
                        type="date"
                        :is-valid="isFormFieldValid('valid_from')"
                    />
                </DCol>
                <DCol sm="2">
                    <DInput
                        v-model="filters.valid_to"
                        :label="__('tools.service_fees.table.valid_to')"
                        type="date"
                        :is-valid="isFormFieldValid('valid_to')"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="filter(serviceFees.current_page, filters)"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
            <DTable
                :items="serviceFees.data"
                :fields="fields"
            >
                <template #valid_from="{item}">
                    <td>
                        {{ item.valid_from !== null ? setDateFormat(item.valid_from) : '' }}
                    </td>
                </template>
                <template #valid_to="{item}">
                    <td>
                        {{ item.valid_to !== null ? setDateFormat(item.valid_to) : '' }}
                    </td>
                </template>
                <template #service_fees="{item}">
                    <td>
                        <DetailsTable
                            :service-fees="item.records"
                            class="border border-top-0"
                        />
                    </td>
                </template>
                <template #active="{item}">
                    <td>
                        <DTag
                            :color="item.status === 'active' ? 'success' : (item.status === 'planned' ? 'primary' : 'danger')"
                            :label="__('tools.service_fees.status.' + item.status)"
                        />
                    </td>
                </template>
                <template #actions="{item}">
                    <td>
                        <div>
                            <DButton
                                v-if="isPlannedServiceFeeExists === false && item.status !== 'planned'"
                                id="duplicate-service-fee"
                                mini
                                icon="content-copy"
                                @click="duplicatePage(item)"
                            />
                            <DButton
                                v-if="item.status === 'planned' || item.status === 'active'"
                                id="edit-service-fee"
                                mini
                                icon="pencil"
                                @click="openEditPage(item)"
                            />
                            <DButton
                                v-if="item.status === 'planned'"
                                id="delete-service-fee"
                                mini
                                icon="delete"
                                color="danger"
                                @click="openDeleteModal(item)"
                            />
                        </div>
                    </td>
                </template>
            </DTable>
        </DCard>
        <DPagination
            v-if="serviceFees.last_page > 1"
            class="pb-2"
            :active-page="serviceFees.current_page"
            :pages="serviceFees.last_page"
            align="center"
            @update:activePage="changePage"
        />
        <DFabs>
            <DFab
                v-if="canCreate"
                id="btn-add-service_fees"
                icon="plus"
                :label="__('tools.service_fees.buttons.add')"
                @click="openCreatePage"
            />
        </DFabs>
        <DModal
            v-if="deleteModal"
            :show="deleteModal"
            is-confirm
            is-error
            :title="__('tools.service_fees.delete_modal.title')"
            :message="__('tools.service_fees.delete_modal.message')"
            @close="deleteModal = false"
            @confirm="deleteServiceFee"
        />
    </div>
</template>

<script>
import DetailsTable from '~@/Components/BillingAndAccounting/ServiceFees/DetailsTable'

export default {
    name: 'ServiceFeesIndex',
    components: {
        DetailsTable
    },
    props: {
        serviceFees: {
            type: Object,
            default: () => { return {} }
        },
        appliedFilters: {
            type: Object,
            default: () => { return {} }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        statusOptions: {
            type: Array,
            default: () => { return [] }
        },
        canCreate: {
            type: Boolean,
            default: true
        },
        marketRegionsOptions: {
            type: Array,
            default: () => { return [] }
        },
        isPlannedServiceFeeExists: {
            type: Boolean,
            default: () => { return false }
        },
    },
    data () {
        return {
            fields: [
                { key: 'valid_from', label: this.__('tools.service_fees.table.valid_from') },
                { key: 'valid_to', label: this.__('tools.service_fees.table.valid_to') },
                { key: 'service_fees', label: '' },
                { key: 'active', label: this.__('tools.service_fees.table.status') },
                { key: 'actions', label: '' }
            ],
            deleteModal: false,
            filters: {
                status: this.appliedFilters.status ?? 'all',
                valid_from: this.appliedFilters.valid_from ?? null,
                valid_to: this.appliedFilters.valid_to ?? null,
                market_regions: this.appliedFilters.market_regions ?? [],
            },
            serviceFee: {}
        }
    },
    computed: {
        statusOptionsMapped () {
            return this.statusOptions.map((option) => {
                return {
                    label: this.__('tools.service_fees.status.' + option),
                    value: option
                }
            })
        },
        marketRegionOptions () {
            return this.marketRegionsOptions.map((item) => {
                return {
                    value: item.value,
                    text: item.label,
                    selected: this.appliedFilters.market_regions?.includes(String(item.value))
                }
            })
        }
    },
    methods: {
        changePage (page) {
            this.filter(page, this.filters)
        },
        filter (page, filters, defaultFilters = false) {
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            const routeParameters = {
                saveFilters: true
            }

            if (page > 1) {
                queryParameters.page = page
            }

            if (defaultFilters === true) {
                routeParameters.defaultFilters = true
            }

            this.$inertia.get(this.$route('tools.service-fees.index', routeParameters), queryParameters)
        },
        resetFilters () {
            this.filter(this.serviceFees.current_page, {}, true)
        },
        openDeleteModal (item) {
            this.serviceFee = item
            this.deleteModal = true
        },
        openCreatePage () {
            this.$inertia.get(this.$route('tools.service-fees.create'))
        },
        openEditPage (item) {
            this.$inertia.get(this.$route('tools.service-fees.edit', {
                service_fee: item.id
            }))
        },
        deleteServiceFee () {
            this.$inertia.delete(this.$route('tools.service-fees.destroy', {
                service_fee: this.serviceFee.id
            }))
        },
        updateMarketRegions (values) {
            this.filters.market_regions = values
        },
        duplicatePage (item) {
            this.$inertia.get(this.$route('tools.service-fees.create', {
                service_fee: item.id
            }))
        }
    }
}
</script>
